import { useState } from "react";
import { Trophy, Phone } from "lucide-react";
import { reais, roi, dataBr, pctCurto, agendadaNoFuturo } from "./api";
import { ACENTO } from "./cores";

/**
 * Tabela de campanhas do painel (a protagonista): cada linha e' o resumo da
 * campanha e clica pro relatorio. TOP 5 por padrao; o botao embaixo mostra
 * todas as enviadas.
 *
 * Faturamento = cruzamento (quem recebeu e pediu, + link no SMS). No SMS a
 * venda so' pelo link aparece embaixo, como detalhe.
 */
export default function RankingCampanhas({ ranking, ehVoz, onAbrirCampanha }) {
  const [mostrarTodas, setMostrarTodas] = useState(false);
  const maiorReceita = Math.max(...ranking.map((x) => x.receita_centavos), 1);
  const listaRanking = mostrarTodas ? ranking : ranking.slice(0, 5);

  return (
    <div className="rounded-xl border bg-card p-5">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        <Trophy className="w-4 h-4 text-amber-500" />
        {mostrarTodas ? "Todas as campanhas" : "Top 5 — campanhas que mais renderam"}
      </h3>
      <p className="text-xs text-muted-foreground -mt-2 mb-4">
        Faturamento = pedidos de quem recebeu {ehVoz ? "a ligação" : "o SMS ou comprou pelo link"}, até 7 dias
        depois (telefone cruzado com o do pedido). Quem recebeu duas campanhas conta nas duas; o total lá em cima
        conta uma vez só.
      </p>
      {!ranking.length ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Nenhuma campanha enviada ainda. Clique em "Nova campanha" para disparar a primeira —
          cada uma aparece aqui com envios, gasto, faturamento e retorno.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className={`w-full text-sm ${ehVoz ? "min-w-[640px]" : "min-w-[860px]"}`}>
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left font-medium pb-2">Campanha</th>
                <th className="text-center font-medium pb-2">{ehVoz ? "Ligações feitas" : "SMS enviados"}</th>
                {!ehVoz && <th className="text-center font-medium pb-2">Cliques</th>}
                <th className="text-center font-medium pb-2">Valor gasto</th>
                <th className="text-center font-medium pb-2">Faturamento</th>
                {!ehVoz && <th className="text-center font-medium pb-2">Ticket médio</th>}
                <th className="text-center font-medium pb-2">ROI</th>
              </tr>
            </thead>
            <tbody>
              {listaRanking.map((c) => (
                <tr key={c.id} onClick={() => onAbrirCampanha?.(c.id)}
                  className="border-t cursor-pointer hover:bg-muted/30 transition-colors">
                  <td className="py-3 pr-3">
                    <p className="font-medium truncate max-w-[240px] flex items-center gap-1.5">
                      {c.canal === "voz" && <Phone className="w-3.5 h-3.5 text-primary shrink-0" />}
                      {c.nome}
                    </p>
                    {agendadaNoFuturo(c.agendada_para) ? (
                      <p className="text-[11px] text-amber-500 tabular-nums font-medium">
                        Agendada para {dataBr(c.agendada_para)}
                      </p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground tabular-nums">
                        {dataBr(c.enviada_em)} · {pctCurto(c.taxa_entrega_decimos)}{" "}
                        {c.canal === "voz" ? "discadas" : "de entrega"}
                      </p>
                    )}
                    <div className="h-1 rounded-full mt-1.5 bg-muted overflow-hidden max-w-[240px]">
                      <div className="h-full rounded-full"
                        style={{ width: `${Math.max((c.receita_centavos / maiorReceita) * 100, 2)}%`, background: ACENTO }} />
                    </div>
                  </td>
                  <td className="text-center tabular-nums">
                    {c.enviados.toLocaleString("pt-BR")}
                    {!ehVoz && <span className="text-muted-foreground"> / {(c.entregues || 0).toLocaleString("pt-BR")} entregues</span>}
                  </td>
                  {!ehVoz && <td className="text-center tabular-nums">{(c.cliques || 0).toLocaleString("pt-BR")}</td>}
                  <td className="text-center tabular-nums">{reais(c.custo_centavos)}</td>
                  <td className="text-center tabular-nums font-semibold">
                    {(c.pedidos || 0).toLocaleString("pt-BR")}
                    <span className="text-muted-foreground font-normal">
                      {" "}{ehVoz ? (c.pedidos === 1 ? "pedido" : "pedidos") : (c.pedidos === 1 ? "venda" : "vendas")} /{" "}
                    </span>
                    {reais(c.receita_centavos)}
                    {!ehVoz && c.pedidos_link > 0 && (
                      <p className="text-[11px] text-muted-foreground font-normal">
                        {c.pedidos_link} pelo link · {reais(c.receita_link_centavos)}
                      </p>
                    )}
                  </td>
                  {!ehVoz && <td className="text-center tabular-nums">
                    {c.ticket_medio_centavos === null ? "—" : reais(c.ticket_medio_centavos)}
                  </td>}
                  <td className="text-center tabular-nums text-primary font-semibold">
                    {c.roi_centesimos !== null ? roi(c.roi_centesimos).replace(",0x", "x") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {ranking.length > 5 && (
            <div className="pt-3 text-center border-t mt-1">
              <button onClick={() => setMostrarTodas((v) => !v)}
                className="text-sm font-medium text-primary hover:underline">
                {mostrarTodas ? "Mostrar só o top 5"
                  : `Ver todas as campanhas enviadas (${ranking.length})`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
